<!--
  Command Center view "Insights" (stored value "classicCgs"; it was called Classic CGS): the CGS2022
  dashboard's production cards in swiftApp.

  WHAT CAME ACROSS UNCHANGED (CGS2022 wwwroot/js/dashboardGridItems.js)
  - Funded This Year, Current Locked Pipeline, Total Active Pipeline: the same
    api/Insights/GetInsightsData bodies (queryFields / filterRule) the CGS summary cards send,
    with the acting user's azureId. Units and volume are the API's figures, not recomputed here.
  - The production chart: the already-ported OriginationChart (views/insights/origination), which
    sends CGS's four chart requests (this year, last year, and the two month-to-date overlays).

  WHAT CHANGED, ON PURPOSE
  - One bar above the chart holds every metric (CGS's seven, in CGS's order) and Units / Volume, all
    visible as buttons instead of a drop-down. It drives OriginationChart through its metricName prop.
  - No tasks widget on this view (asked for 10/17); tasks live on Prospect and the Originate view.
  - The average loan size under each figure is volume ÷ units, worked out here from the two API
    figures. It's labelled "avg" and hidden when units is 0.
-->
<template>
  <div class="cx cxc">
    <section class="cxc-figs" aria-label="Production summary">
      <div v-for="(f, i) in figures" :key="f.key" class="cx-grp" :class="{ 'cx-grp--violet': i === 2 }">
        <div class="cx-grp-t"><i :class="f.icon" aria-hidden="true"></i> {{ f.title }}</div>
        <div v-if="f.loading" class="cx-skel" aria-hidden="true"><span style="width: 60%; height: 22px;"></span><span style="width: 40%;"></span></div>
        <div v-else-if="f.error" class="cx-banner cx-banner--error" role="alert"><i class="fa-solid fa-circle-exclamation"></i> {{ f.error }}</div>
        <div v-else class="cx-tiles" style="grid-template-columns: repeat(2, minmax(0, 1fr));">
          <div class="cx-tile"><div class="cx-tile-l">Units</div><div class="cx-tile-v">{{ fmtInt(f.units) }}</div></div>
          <div class="cx-tile"><div class="cx-tile-l">Volume</div><div class="cx-tile-v">{{ fmtShort(f.volume) }}</div></div>
        </div>
        <div class="cx-foot">
          <span>{{ f.loading || f.error ? '' : fmtMoney(f.volume) }}</span>
          <span>{{ avgLabel(f) }}</span>
        </div>
      </div>
    </section>

    <section class="cxc-chart" aria-label="Production chart">
      <div class="cx-seg cxc-bar" role="group" aria-label="Chart">
        <button v-for="m in METRICS" :key="m" type="button" :class="{ on: metric === m }" :aria-pressed="metric === m" @click="metric = m">{{ m }}</button>
        <span class="cxc-bar-gap" aria-hidden="true"></span>
        <button type="button" :class="{ on: measure === 'Units' }" :aria-pressed="measure === 'Units'" @click="measure = 'Units'">Units</button>
        <button type="button" :class="{ on: measure === 'Volume' }" :aria-pressed="measure === 'Volume'" @click="measure = 'Volume'">Volume</button>
      </div>
      <OriginationChart :key="measure" :units-or-volume="measure" :filters="chartFilters" :metric-name="metric" />
    </section>
  </div>
</template>

<script>
import '@/assets/css/connect.css'
import api from '@/api/connect.service.js'
import insightsApi from '@/api/insights.service.js'
import OriginationChart from '@/views/insights/origination/OriginationChart.vue'
import { emptyChartFilters } from '@/views/insights/origination/originationQueries.js'

// The chart's metrics in CGS2022's order (summaryChartCard.vue); names match CHART_METRICS.
const METRICS = ['Credit Pulled', 'PreQual', 'Applications', 'Rate Locks', 'Submitted To UW', 'Funded', 'Purchased']

const EMPTY_LISTS = () => ({ channel: [], branch: [], state: [], lo: [], bda: [], lien: [] })

// The three CGS2022 summary cards, verbatim from dashboardGridItems.js.
function figureBodies() {
  const year = new Date().getFullYear()
  const thisYearStart = new Date(year, 0, 1)
  const thisYearEnd = new Date(year, 11, 31)
  const milestoneNot = value => ({ condition: 'and', id: 'CurrentMilestone', operator: '!=', type: 'String', input: 'select', value, rules: [] })
  return {
    funded: {
      ...EMPTY_LISTS(),
      queryFields: [
        { field: 'MS_FUN', type: 'Date', oper: '>=', val: thisYearStart },
        { field: 'MS_FUN', type: 'Date', oper: '<=', val: thisYearEnd }
      ]
    },
    locked: {
      ...EMPTY_LISTS(),
      queryFields: [
        { field: 'F1393', type: 'String', oper: '=', val: 'Active Loan' },
        { field: 'lockStatus', type: 'String', oper: '=', val: 'Locked' },
        { field: 'LoanFolder', type: 'String', oper: '!=', val: 'Training' }
      ]
    },
    active: {
      ...EMPTY_LISTS(),
      filterRule: {
        condition: 'and',
        rules: [
          {
            rules: [
              milestoneNot('Started'),
              milestoneNot('Ready for Disclosures'),
              milestoneNot('Package Received'),
              milestoneNot('File Management'),
              milestoneNot('Correspondent Funding'),
              { condition: 'and', id: 'F1393', operator: '=', type: 'String', input: 'select', value: 'Active Loan', rules: [] },
              { id: 'F11', operator: '!=', type: 'String', input: 'select', value: 'TBD', rules: [] }
            ]
          },
          { rules: [{ condition: 'or', id: 'LoanFolder', operator: '=', type: 'String', input: 'select', value: 'My Pipeline' }] }
        ]
      }
    }
  }
}

export default {
  name: 'ClassicCgsDashboard',
  components: { OriginationChart },
  data() {
    return {
      figures: [
        { key: 'funded', title: 'Funded This Year', icon: 'fa-duotone fa-solid fa-circle-check', units: 0, volume: 0, loading: true, error: '' },
        { key: 'locked', title: 'Current Locked Pipeline', icon: 'fa-duotone fa-solid fa-lock', units: 0, volume: 0, loading: true, error: '' },
        { key: 'active', title: 'Total Active Pipeline', icon: 'fa-duotone fa-solid fa-chart-network', units: 0, volume: 0, loading: true, error: '' }
      ],
      METRICS,
      metric: 'Funded',
      measure: 'Units',
      chartFilters: emptyChartFilters()
    }
  },
  mounted() {
    this.loadFigures()
  },
  methods: {
    fmtInt(v) { return Number(v || 0).toLocaleString() },
    fmtMoney(v) { return Number(v || 0).toLocaleString(undefined, { style: 'currency', currency: 'USD' }) },
    fmtShort(v) {
      const n = Number(v || 0)
      if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`
      if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`
      if (n >= 1e3) return `$${Math.round(n / 1e3)}K`
      return `$${Math.round(n)}`
    },
    avgLabel(f) {
      const u = Number(f.units || 0)
      if (f.loading || f.error || !u) return ''
      return `avg ${this.fmtShort(Number(f.volume || 0) / u)}`
    },
    async loadFigures() {
      const bodies = figureBodies()
      const azureId = api.actingAzureId()
      await Promise.all(this.figures.map(async f => {
        f.loading = true
        f.error = ''
        try {
          const res = await insightsApi.insights('GetInsightsData', { ...bodies[f.key], azureId })
          if (res && res.isSuccess === false) throw new Error(res.message || 'Please try again')
          const data = (res && res.data) || {}
          f.units = Number(data.units || 0)
          f.volume = Number(data.volume || 0)
        } catch (e) {
          f.error = e.message || 'Please try again'
        } finally {
          f.loading = false
        }
      }))
    }
  }
}
</script>

<style scoped>
.cxc { display: flex; flex-direction: column; gap: 14px; width: 100%; }
.cxc-figs { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
@media (max-width: 900px) { .cxc-figs { grid-template-columns: minmax(0, 1fr); } }
.cxc-chart { min-width: 0; display: flex; flex-direction: column; gap: 10px; }
/* One long bar: the seven metrics, a gap, then Units / Volume. Buttons share the width and wrap on a phone. */
.cxc-bar { display: flex; flex-wrap: wrap; width: 100%; border-radius: 12px; }
.cxc-bar > button { flex: 1 1 auto; justify-content: center; white-space: nowrap; }
.cxc-bar-gap { flex: 0 0 1px; align-self: stretch; margin: 4px 6px; background: #e6e8ec; }
</style>
